import React, { useState } from "react";
import * as Icon from "react-feather";
import { Pagination, Avatar } from "antd";
import styled from "styled-components";

import NoData from "components/no-data/NoData";
import {
  numberWithCommas,
  getInitials,
  formatDate,
  isFunction,
  convertCurrency,
  dateFormat,
} from "utils/helpers";
import { file_url } from "services/apiSrv";

// Styled components
const StyledTableWrapper = styled.div`
  margin-bottom: 50px;
`;

const TableTitle = styled.h1`
  font-size: 20px;
  font-weight: 600;
  margin-bottom: 12px;
  color: #001f5f;
`;

// NEW: Container to enable rounded corners on collapsed table
const TableContainer = styled.div`
  border-top-left-radius: 18px;
  border-top-right-radius: 18px;
  overflowY: "auto";
  border: 1px solid #eee;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
`;

const StyledTable = styled.table`
  width: 100%;
  border-collapse: collapse;
  background: #fff;

  thead {
    background-color: #669bbc;
    color: #fff;
    text-transform: uppercase;
    font-size: 12px;
    font-weight: bold;
  }

  th,
  td {
    padding: 4px 8px;
    font-size: 0.75em;
    text-align: left;
    border-bottom: 1px solid #eee;
  }

  tbody tr:hover {
    background-color: #f7f9fc;
  }

  tbody tr:last-child td {
    border-bottom: none;
  }
`;

const TableImage = styled.img`
  height: 40px;
  width: 40px;
  object-fit: contain;
  border-radius: 50%;
`;

const Badge = styled.div`
  display: inline-block;
  padding: 4px 8px;
  border-radius: 12px;
  font-size: 12px;
  font-weight: 500;
  color: #000000;

  &.closed {
    background: #198754;
    color: white;
  }
  &.open {
    background: #ffc107;
    color: #212529;
  }
  &.new {
    background: #dc3545;
    color: white;
  }
  &.default {
    background: #6c757d;
    color: white;
  }
  &.success {
    background: #6c1881;
    color: white;
  }
  &.warning {
    background: #1886c1;
    color: white;
  }
`;

const ActionButton = styled.button`
  background: #0056b3;
  border: none;
  color: #fff;
  margn-top: 16px;
  padding: 2px 16px;
  font-size: 10px;
  border-radius: 46px;
  cursor: pointer;

  &:disabled {
    background: #999;
    cursor: not-allowed;
  }
`;

const TableComponent = (props) => {
  function getNestedObject(nestedObj, pathArr) {
    return pathArr.reduce(
      (obj, key) => (obj && obj[key] !== "undefined" ? obj[key] : undefined),
      nestedObj
    );
  }

  const {
    table_fields = [],
    table_data = [],
    row_data = [],
    title = "Table Title",
    pageSize = 10,
  } = props;

  const [paginationData, setPaginationData] = useState({
    totalPage: Math.ceil(table_data.length / pageSize),
    current: 1,
    minIndex: 0,
    maxIndex: pageSize,
  });

  const handlePaginationChange = (page) => {
    setPaginationData({
      ...paginationData,
      current: page,
      minIndex: (page - 1) * pageSize,
      maxIndex: page * pageSize,
    });
  };

  // console.log("====dateLogged===",dateFormat([row_data[5].field]) )


  return (
    <div style={{ overflowY: "auto"}}>
      <StyledTableWrapper>
        <TableTitle>{title}</TableTitle>

        {table_data.length === 0 ? (
          <NoData message="No Data" />
        ) : (
          <TableContainer>
            <StyledTable>
              <thead>
                <tr>
                  {table_fields.map((field, i) => (
                    <th key={i}>{field.name}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {table_data
                  .slice(paginationData.minIndex, paginationData.maxIndex)
                  .map((d, i) => (
                    <tr key={i + paginationData.minIndex} style={{ overflowY: "auto" }}>
                      {row_data?.map((_, i) => (
                        <td key={i}>
                          {row_data[i].type === "image" ? (
                            <Avatar
                              size={40}
                              src={`${
                                d[row_data[i].domain] || file_url || ""
                              }${d[row_data[i].field]}`}
                              style={{
                                color: "#001f5f",
                                backgroundColor: "rgba(255,188,183)",
                              }}
                            >
                              <small>{getInitials(d[row_data[1].field])}</small>
                            </Avatar>
                          ) : row_data[i].type === "text" ? (
                            <div>
                              {getNestedObject(
                                d,
                                row_data[i].field.split(".")
                              ) || "-"}
                            </div>
                          ) : row_data[i].type === "number" ? (
                            <div>
                              {numberWithCommas(
                                Math.round(
                                  Number(d[row_data[i].field]) * 100
                                ) / 100
                              )}
                            </div>
                          ) : row_data[i].type === "currency" ? (
                            <div>{convertCurrency(d[row_data[i].field])}</div>
                          ) : row_data[i].type === "nested" ? (
                            <>
                              {row_data[i].rows.map((row) => (
                                <div key={row.field}>
                                  <span className={row.class}>
                                    {d[row.field] || "-"}
                                  </span>
                                </div>
                              ))}
                            </>
                          ) : row_data[i].type === "status" ? (
                            <Badge
                              className={
                                d[row_data[i].field]?.toLowerCase() || "default"
                              }
                            >
                              <span style={{ fontSize: "0.75em" }}>
                                {d[row_data[i].field]}
                              </span>
                            </Badge>
                          ) : row_data[i].type === "date" ? (
                            <div>  {formatDate(getNestedObject(d, row_data[i].field.split(".")))}</div>
                          ) : row_data[i].type === "action" &&
                            row_data[i].actions.length === 1 ? (
                            row_data[i].actions.map(({ method, field, title }) => {
                              let param = d[field] || "";
                              return (
                                <ActionButton
                                  key={title}
                                  onClick={() =>
                                    isFunction(method) &&
                                    method.apply(this, [param])
                                  }
                                  disabled={
                                    d?.status?.toLowerCase() === "closed"
                                  }
                                >
                                  {title}
                                </ActionButton>
                              );
                            })
                          ) : row_data[i].type === "action" &&
                            row_data[i].actions.length > 1 ? (
                            <div className="dropdown">
                              <a
                                href="#"
                                data-toggle="dropdown"
                                aria-haspopup="true"
                                aria-expanded="false"
                                className="btn btn-sm btn-label-primary"
                              >
                                <Icon.MoreHorizontal />
                              </a>
                              <ul className="dropdown-menu dropdown-menu-left">
                                {row_data[i].actions.map(
                                  ({ method, field, title }, idx) => {
                                    let param = d[field] || "";
                                    return (
                                      <li className="dropdown-item" key={idx}>
                                        <a
                                          href="#"
                                          className="dropdown-link tx-13"
                                          onClick={(e) => {
                                            e.preventDefault();
                                            isFunction(method) &&
                                              method.apply(this, [param]);
                                          }}
                                        >
                                          <Icon.Eye size={14} /> {title}
                                        </a>
                                      </li>
                                    );
                                  }
                                )}
                              </ul>
                            </div>
                          ) : row_data[i].type === "boolean" ? (
                            <div>
                              {d[row_data[i].field] ? "Yes" : "No"}
                            </div>
                          ) : null}
                        </td>
                      ))}
                    </tr>
                  ))}
              </tbody>
            </StyledTable>
          </TableContainer>
        )}
      </StyledTableWrapper>

      <Pagination
        responsive={true}
        total={table_data.length}
        showTotal={(total, range) =>
          `${range[0]}-${range[1]} of ${total} items`
        }
        pageSize={pageSize}
        current={paginationData.current}
        onChange={handlePaginationChange}
      />
    </div>
  );
};

export default TableComponent;